import * as React from 'react';
import { Component } from 'react';
import { observable, computed, action } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import { isDisabled } from '../utils/customPropStyles';
import FundManagerImage from './FundManagerImage';
import FundManagerContent from './FundManagerContent';
import Icon from './Icon';
import { DataStore, AccessibilityStore, EntityStore } from '../stores';
import { FundManager } from '../models/entity';
import { FundManagers as FundManagersConfig } from '../models/configuration/components/FundManagers';
import HTag from './HTag';
import { IQueryContext } from '@kurtosys/ksys-api-client/dist/models/commonTypes';
import { sortValues } from '../utils';
import { Theme } from '../models/Theme';

const FundManagersWrapper = styled<any, any>(
    'div',
    'fundManagersWrapper',
    'fundManagers'
)`
    ${propStyles};
    position: relative;
`;

FundManagersWrapper.defaultProps = {};

const FundManagersNavArrows = styled<any, any>(
    'li',
    'fundManagersNavArrows',
    'fundManagers'
)`
    ${propStyles};
    position: absolute;
    left: 0;
    right: 0;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    opacity: 0;
    transition: opacity 300ms linear;
`;

FundManagersNavArrows.defaultProps = {};

const FundManagersList = styled<any, any>(
    'ol',
    'fundManagersList',
    'fundManagers'
)`
    ${propStyles};
    list-style: none;
    &:hover {
        ${FundManagersNavArrows} {
            opacity: 1;
        }
    }
`;

FundManagersList.defaultProps = {
    position: 'relative',
    display: 'flex',
    alignItems: ['flex-start', 'center'],
    flexWrap: 'nowrap',
    overflowX: 'hidden',
    marginY: ['0', 'md'],
};

const FundManagersListItem = styled<any, any>(
    'li',
    'fundManagersListItem',
    'fundManagers'
)`
    ${propStyles};
    transition: transform 300ms ease-in-out;
    transform: ${p => `translate3d(${-100 * p.activeSlide}%, 0, 0)`};
`;

FundManagersListItem.defaultProps = {
    display: 'flex',
    flex: '0 0 auto',
    maxWidth: '100%',
    flexDirection: ['row', 'column'],
    alignItems: ['flex-start', 'stretch'],
    bgColor: 'white',
    borderWidth: '1px',
    borderStyle: ['none none solid none', 'solid'],
    borderColor: 'gray300',
    borderRadius: ['0', 'sm'],
    boxShadow: ['none', 'sm'],
    padding: 'md',
};

const FundManagerDetails = styled<any, any>(
    'div',
    'fundManagerDetails',
    'fundManagers'
)`
    ${propStyles};
`;

FundManagerDetails.defaultProps = {
    margin: ['0 0 0 1em', '0'],
    width: '100%',
};

const FundManagersNav = styled<any, any>(
    'nav',
    'fundManagersNav',
    'fundManagers'
)`
    ${propStyles};
`;

FundManagersNav.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginY: 'md',
};

const FundManagersNavButton = styled<any, any>(
    'button',
    'fundManagersNavButton',
    'fundManagers'
)`
    ${propStyles};
    ${isDisabled};
    outline: none;
    cursor: pointer;
    &:disabled {
        opacity: 0.25;
    }
`;

FundManagersNavButton.defaultProps = {
    bgColor: 'transparent',
    borderStyle: 'none',
    fontSize: 'lg',
    padding: '0 0.1em',
    color: 'gray500',
    selected: {
        color: 'body',
    },
};

interface FundManagersProps {
    theme?: Theme;
    dataStore?: DataStore;
    accessibilityStore?: AccessibilityStore;
    entityStore?: EntityStore;
    showHeading: boolean;
    fundManagers: Array<FundManager>;
    imageSrc?: string;
    overrideConfig?: FundManagersConfig;
}

@inject('dataStore', 'accessibilityStore', 'entityStore')
@observer
class FundManagers extends Component<FundManagersProps> {
    autoPlayId: number;
    @observable activeSlide: number = 0;

    constructor(props) {
        super(props);

        if (!this.props.dataStore.isMobile && this.autoPlayInterval > 0) {
            this.autoPlay(this.autoPlayInterval);
        }
    }

    componentWillUnmount() {
        clearInterval(this.autoPlayId);
    }

    @computed
    get fundManagersConfig(): FundManagersConfig {
        const { overrideConfig } = this.props;
        return (
            overrideConfig ||
            this.props.dataStore.config.rawRowConfig.fundManagers
        );
    }

    @computed
    get queryContext(): IQueryContext {
        const { entityStore } = this.props;
        const { queryContext = {} } = entityStore;
        return queryContext;
    }

    @computed
    get isCarousel(): boolean {
        const { carousel = true } = this.fundManagersConfig;
        return carousel;
    }

    @computed
    get label(): string {
        const { label } = this.fundManagersConfig;
        return label;
    }

    @computed
    get autoPlayInterval(): number {
        const { autoPlay = 0 } = this.fundManagersConfig;
        return this.isCarousel ? autoPlay : 0;
    }

    @computed
    get sortedFundManagers() {
        const { sort } = this.fundManagersConfig;
        const { fundManagers } = this.props;

        return sortValues(fundManagers, sort);
    }

    @action
    setActiveSlide = (index: number) => {
        this.activeSlide = index;
        this.autoPlay(this.autoPlayInterval);
    };

    @computed
    get prevSlideDisabled(): boolean {
        return this.activeSlide === 0;
    }

    @computed
    get nextSlideDisabled(): boolean {
        return this.activeSlide === this.sortedFundManagers.length - 1;
    }

    @action
    prevSlide = () => {
        this.activeSlide = this.prevSlideDisabled
            ? this.sortedFundManagers.length - 1
            : this.activeSlide - 1;
        this.autoPlay(this.autoPlayInterval);
    };

    @action
    nextSlide = () => {
        this.activeSlide = this.nextSlideDisabled ? 0 : this.activeSlide + 1;
        this.autoPlay(this.autoPlayInterval);
    };

    @action
    autoPlay = t => {
        if (t > 0) {
            if (this.autoPlayId) {
                clearInterval(this.autoPlayId);
            }
            this.autoPlayId = setInterval(this.nextSlide, t) as any;
        }
    };

    render() {
        const {
            theme,
            dataStore,
            imageSrc,
            showHeading,
            accessibilityStore,
        } = this.props;
        const {
            sortedFundManagers,
            setActiveSlide,
            prevSlide,
            nextSlide,
            activeSlide,
            prevSlideDisabled,
            nextSlideDisabled,
            isCarousel,
        } = this;

        return (
            <FundManagersWrapper {...theme.FundManagersWrapper}>
                {showHeading && (
                    <HTag
                        componentTheme={'FundManagersHeading'}
                        defaultStyleProps={{
                            color: 'body',
                            fontFamily: 'heading',
                            fontWeight: 'bold',
                            fontSize: 'md',
                        }}
                        level={2}
                        text={this.label}
                    />
                )}
                <FundManagersList
                    flexDirection={isCarousel ? ['column', 'row'] : 'column'}
                    {...theme.FundManagersList}
                >
                    {!dataStore.isMobile &&
                        isCarousel &&
                        sortedFundManagers.length > 1 && (
                            <FundManagersNavArrows
                                {...theme.FundManagersNavArrows}
                            >
                                <FundManagersNavButton
                                    aria-label={accessibilityStore.buttonAccessibilityValue(
                                        'FundManagersNavButtonPrevious'
                                    )}
                                    onClick={() => prevSlide()}
                                    disabled={prevSlideDisabled}
                                    padding="sm"
                                    data-metric-id={
                                        'fundManagerNav-button-previous'
                                    }
                                    {...theme.FundManagersNavArrowButton}
                                >
                                    <Icon fileName="FundManagersNavArrowLeftIcon" />
                                </FundManagersNavButton>
                                <FundManagersNavButton
                                    aria-label={accessibilityStore.buttonAccessibilityValue(
                                        'FundManagersNavButtonNext'
                                    )}
                                    onClick={() => nextSlide()}
                                    disabled={nextSlideDisabled}
                                    padding="sm"
                                    data-metric-id={
                                        'fundManagerNav-button-next'
                                    }
                                    {...theme.FundManagersNavArrowButton}
                                >
                                    <Icon fileName="FundManagersNavArrowRightIcon" />
                                </FundManagersNavButton>
                            </FundManagersNavArrows>
                        )}
                    {sortedFundManagers.map((fundManager, index) => {
                        const fundManagerContent = (
                            <FundManagerContent
                                key={`${fundManager.code}-content`}
                                fundManager={fundManager}
                                config={this.fundManagersConfig}
                            />
                        );
                        return (
                            <FundManagersListItem
                                key={fundManager.code}
                                data-selected={activeSlide === index}
                                activeSlide={activeSlide}
                                {...theme.FundManagersListItem}
                            >
                                <FundManagerImage
                                    fundManager={fundManager}
                                    orchestrationImageSrc={imageSrc}
                                    config={this.fundManagersConfig}
                                />
                                {dataStore.isMobile ? (
                                    <FundManagerDetails
                                        {...theme.FundManagerDetailsMobile}
                                    >
                                        {fundManagerContent}
                                    </FundManagerDetails>
                                ) : (
                                    fundManagerContent
                                )}
                            </FundManagersListItem>
                        );
                    })}
                </FundManagersList>
                {!dataStore.isMobile &&
                    isCarousel &&
                    sortedFundManagers.length > 1 && (
                        <FundManagersNav {...theme.FundManagersNav}>
                            {sortedFundManagers.map((fm, index) => (
                                <FundManagersNavButton
                                    aria-label={accessibilityStore.buttonAccessibilityValue(
                                        'FundManagersNavButton'
                                    )}
                                    key={fm.code}
                                    onClick={() => setActiveSlide(index)}
                                    data-selected={activeSlide === index}
                                    data-metric-id={`fundManagerNav-button-${index}`}
                                    {...theme.FundManagersNavButton}
                                >
                                    {'●'}
                                </FundManagersNavButton>
                            ))}
                        </FundManagersNav>
                    )}
            </FundManagersWrapper>
        );
    }
}

export default withTheme(FundManagers) as any;



// WEBPACK FOOTER //
// ./src/Components/FundManagers.tsx